/**
 * What the Sylectus writer did with every email in the window (ADR-034): the funnel, how fast, and how its places were
 * identified. From the writer's own hourly counters, so it is the writer's account of itself, not a re-derivation.
 */

import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Card from '@mui/joy/Card'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import { loadPipelineApi } from 'api/loadPipeline'
import { ErrorState, Loading, Stat, StatRow, formatNumber, useLoadPipelineData } from './shared'

/** "3 min ago", "2 h ago": how long the writer has been quiet. */
const quietFor = (lastEventAt: string | null): string => {
    if (!lastEventAt) return 'no email in this window'
    const minutes = Math.max(0, Math.round((Date.now() - Date.parse(lastEventAt)) / 60_000))
    return minutes < 90 ? `${minutes} min ago` : `${Math.round(minutes / 60)} h ago`
}

const percent = (share: number): string => `${(share * 100).toFixed(share > 0 && share < 0.1 ? 1 : 0)}%`
const duration = (seconds: number): string => (seconds < 90 ? `${Math.round(seconds)} s` : `${(seconds / 60).toFixed(1)} min`)

export default function SylectusActivity({ hours }: { hours: number }) {
    const { data, error, loading } = useLoadPipelineData(() => loadPipelineApi.getSylectusActivity(hours), [hours])
    if (error) return <ErrorState error={error} />
    if (loading && !data) return <Loading />
    if (!data) return null

    const { totals } = data
    const quiet = quietFor(totals.lastEventAt)

    return (
        <>
            <StatRow>
                <Stat label="Last email" value={quiet} hint="the writer finished it, whatever the outcome" tone={totals.lastEventAt ? 'neutral' : 'warning'} />
                <Stat label="Published" value={formatNumber(totals.published)} hint="a SEEN load update on the load topic" tone="success" />
                <Stat label="Duplicate copies" value={formatNumber(totals.suppressed)} hint="the same posting through another mailbox" />
                <Stat label="Not a load" value={formatNumber(totals.ignored)} hint="notices with no posting in them" />
                <Stat
                    label="Refused"
                    value={formatNumber(totals.refused)}
                    hint={`${percent(totals.refusalRate)} of loads that were not copies`}
                    tone={totals.refusalRate > 0.2 ? 'warning' : 'neutral'}
                />
                <Stat
                    label="Failed attempts"
                    value={formatNumber(totals.failed)}
                    hint="geocoding or the topic was down; each is redelivered, not lost"
                    tone={totals.failed > 0 ? 'danger' : 'neutral'}
                />
            </StatRow>
            <StatRow>
                <Stat
                    label="Posted to published"
                    value={totals.avgLagSeconds === null ? '—' : duration(totals.avgLagSeconds)}
                    hint={`average; ${percent(totals.slowShare)} took over 5 min`}
                    tone={totals.slowShare > 0.1 ? 'warning' : 'neutral'}
                />
                <Stat
                    label="Places identified by name"
                    value={totals.byNameShare === null ? '—' : percent(totals.byNameShare)}
                    hint="the rest by ZIP or point, so their id differs from DAT's for the same city"
                />
            </StatRow>

            {data.hours.length === 0 ? (
                <Alert color="warning" variant="soft" sx={{ mb: 3 }}>
                    No email was handled in this window. Sylectus traffic is quiet at night and on weekends; if it should not be, check the service and the
                    dead-letter alert.
                </Alert>
            ) : null}

            <Card sx={{ mb: 3 }}>
                <Typography level="title-md" sx={{ mb: 1 }}>
                    By hour (UTC)
                </Typography>
                <Sheet sx={{ overflow: 'auto', maxHeight: 360 }}>
                    <Table size="sm" stickyHeader>
                        <thead>
                            <tr>
                                <th>Hour</th>
                                <th>Handled</th>
                                <th>Published</th>
                                <th>Copies</th>
                                <th>Not a load</th>
                                <th>Refused</th>
                                <th>Failed</th>
                                <th>Avg lag</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...data.hours].reverse().map((h) => (
                                <tr key={h.hourStart}>
                                    <td>
                                        <Box sx={{ fontFamily: 'monospace' }}>{h.hourStart.slice(0, 13).replace('T', ' ')}:00</Box>
                                    </td>
                                    <td>{formatNumber(h.handled)}</td>
                                    <td>{formatNumber(h.published)}</td>
                                    <td>{formatNumber(h.suppressed)}</td>
                                    <td>{formatNumber(h.ignored)}</td>
                                    <td>{formatNumber(h.refused)}</td>
                                    <td>{h.failed > 0 ? <b>{formatNumber(h.failed)}</b> : 0}</td>
                                    <td>{h.lagCount === 0 ? '—' : duration(h.lagSumSeconds / h.lagCount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
            </Card>
        </>
    )
}
